import { jsxs, jsx } from "react/jsx-runtime";
import { ErrorSmall } from "@wix/wix-ui-icons-common/on-stage";
import { Popover, PopoverTriggerAction, TextButton, Text } from "@wix/wix-ui-tpa/cssVars";
import React__default, { useRef } from "react";
import { useConfig } from "../../../../../contexts/config-context.js";
import { useTranslation } from "../../../../../contexts/translation-context.js";
import { useId } from "../../../../../hooks/use-id.js";
import { DATA_HOOKS } from "../dataHooks.js";
import { classes, st } from "../FileUpload.st.css";
import File from "./File.js";
import { useFocusWhen } from "./use-focus-when.js";
import { useTrapFocus } from "./use-trap-focus.js";
const fileKey = (f) => f.file.name;
const FileUploadPopover = ({
  files,
  onDeleteClick,
  explanationText,
  hasError,
  setTextBelowButtonRef
}) => {
  const [showFiles, setShowFiles] = React__default.useState(false);
  const { t } = useTranslation();
  const { isRTL: rtl } = useConfig();
  const contentId = useId();
  const contentRef = useRef(null);
  const textButtonRef = useRef(null);
  useFocusWhen(() => !showFiles, textButtonRef);
  useTrapFocus({
    active: showFiles,
    container: contentRef
  });
  const deleteButtonRefs = useRef({});
  return /* @__PURE__ */ jsxs(
    Popover,
    {
      appendTo: "parent",
      placement: rtl ? "top-end" : "top-start",
      onClickOutside: () => setShowFiles(false),
      shown: showFiles,
      className: classes.popover,
      triggerAction: PopoverTriggerAction.click,
      width: 280,
      onEscPress: () => {
        setShowFiles(false);
      },
      "aria-describedby": contentId,
      "aria-label": t("file-upload.popover.aria-label"),
      children: [
        /* @__PURE__ */ jsx(Popover.Element, { children: /* @__PURE__ */ jsx(
          TextButton,
          {
            "data-hook": DATA_HOOKS.popoverButton,
            className: st(classes.multipleFileExplanation, {
              error: hasError
            }),
            onClick: () => setShowFiles(!showFiles),
            ref: (ref) => {
              textButtonRef.current = ref;
              setTextBelowButtonRef(ref);
            },
            "aria-haspopup": "dialog",
            "aria-expanded": showFiles,
            role: "region",
            "aria-live": "polite",
            children: explanationText
          }
        ) }),
        /* @__PURE__ */ jsx(Popover.Content, { children: /* @__PURE__ */ jsx(
          "div",
          {
            id: contentId,
            ref: contentRef,
            "data-hook": DATA_HOOKS.popoverContent,
            className: classes.files,
            role: "list",
            children: (files ?? []).map((file, index, allFiles) => /* @__PURE__ */ jsx(
              "div",
              {
                className: classes.multipleFiles,
                "data-hook": DATA_HOOKS.popoverFile,
                children: /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsx(
                    File,
                    {
                      file,
                      onDeleteClick: () => {
                        const currentKey = fileKey(file);
                        if (allFiles.length > 1) {
                          const firstFocusableElement = allFiles.filter((f) => fileKey(f) !== currentKey).map((f) => deleteButtonRefs.current[fileKey(f)]).find(Boolean) ?? textButtonRef;
                          firstFocusableElement.current?.focus();
                        }
                        onDeleteClick?.(index);
                      },
                      dataHook: DATA_HOOKS.popoverFileName,
                      "aria-describedby": `${fileKey(file)}-error`,
                      role: "listitem",
                      deleteButtonRef: deleteButtonRefs.current[fileKey(file)] ??= {
                        current: null
                      },
                      focusFile: index === 0
                    }
                  ),
                  file.errorMessage && /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: classes.fileError,
                      "data-hook": DATA_HOOKS.popoverFileError,
                      children: [
                        /* @__PURE__ */ jsx(ErrorSmall, { className: classes.fileErrorIcon }),
                        /* @__PURE__ */ jsx(
                          Text,
                          {
                            id: `${fileKey(file)}-error`,
                            className: classes.filesErrorMessage,
                            children: file.errorMessage
                          }
                        )
                      ]
                    }
                  )
                ] })
              },
              fileKey(file)
            ))
          }
        ) })
      ]
    }
  );
};
export {
  FileUploadPopover as default
};
//# sourceMappingURL=FileUploadPopover.js.map
